/**
 * What the ✚ button would actually do if you tapped it now.
 *
 * The HUD used to guess — health under 60 %, else whichever of food and water was emptier — while `Game.useItem` used
 * a different rule and, crucially, a *fallback order*: if it cannot serve the lowest bar it serves the next one. So the
 * button could sit crossed out ("nothing for that") while F cheerfully ate a sandwich (owner, Sep 10: "the quick button
 * got stuck to the red crossed icon even though I bought something to eat and drink"). One function now answers for
 * both, so the icon is a promise the game keeps.
 */
import type { Registry } from '../content/registry';
import type { Person } from './entities';

export type Need = 'water' | 'food' | 'hp';
/** nothing is wasted on a stomach that is 95 % full (owner's call) */
export const FULL = 0.95;

/** the emptiest bar that is not already full */
export function lowestNeed(p: Person, full = FULL): Need | undefined {
  const bars: [Need, number][] = [['hp', p.hp / p.maxHp], ['water', p.water], ['food', p.food]];
  bars.sort((a, b) => a[1] - b[1]);
  return bars[0][1] < full ? bars[0][0] : undefined;
}

/** does the pocket hold anything that fills this bar? */
export function carriesFor(reg: Registry, p: Person, key: Need): boolean {
  for (const [id, n] of Object.entries(p.items)) if (n > 0 && (reg.get('item', id)?.restore?.[key] ?? 0) > 0) return true;
  return false;
}

/** the order `useItem` tries: the lowest bar first, then the others, skipping any that is already full */
export function needOrder(p: Person, need: Need, want?: 'food' | 'water', full = FULL): Need[] {
  const all: Need[] = need === 'hp' ? ['hp', 'water', 'food'] : need === 'water' ? ['water', 'food', 'hp'] : ['food', 'water', 'hp'];
  return all.filter((k) => (want ? k === want : k === 'hp' ? p.hp < p.maxHp : k === 'water' ? p.water < full : p.food < full));
}

/**
 * What a tap would serve, and with what. `undefined` means the tap has nothing to give — which is the only case the
 * button should draw itself crossed out.
 */
export function useChoice(reg: Registry, p: Person, want?: 'food' | 'water', full = FULL): { key: Need; id: string } | undefined {
  const need = want ?? lowestNeed(p, full);
  if (!need) return undefined;
  if (want && (want === 'food' ? p.food : p.water) >= full) return undefined;
  for (const key of needOrder(p, need, want, full)) {
    let best: { id: string; v: number } | undefined;
    for (const [id, n] of Object.entries(p.items)) {
      if (n <= 0) continue;
      const v = reg.get('item', id)?.restore?.[key] ?? 0;
      if (v > 0 && (!best || v > best.v)) best = { id, v };
    }
    if (best) return { key, id: best.id };
  }
  return undefined;
}
